import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Typography from '@material-ui/core/Typography';
import { MuiThemeProvider } from '@material-ui/core/styles';
import React, { useState } from 'react';
import { CREATE, showNotification, translate } from 'react-admin';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { isPermissiveAsAccountManager } from '../../const/Role';
import dataProvider from '../../dataProvider';
import { lightTheme } from '../../layout/MyLayout';
import SwitchInput from '../common/SwitchInput';

const contentStyle = { marginLeft: '5em' };

const consentAppPageOpen = () => {
  window.open(process.env.REACT_APP_RSB_API_ENDPOINT + '/consent-app/outlook', null, 'width=1000, height=800');
};

const CalendarServiceConfig = ({ permissions, config, getConfig, translate, showNotification }) => {
  const [loading, setLoading] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [checked, setChecked] = useState(config.calendarService === 2);
  const [pendingChecked, setPendingChecked] = useState(null);
  const [isFocus, setIsFocus] = useState(true);

  // calendarServiceが0(EWS)または2(Graph)以外の場合はスイッチを無効化
  const isUnsupportedService = config.calendarService !== 0 && config.calendarService !== 2;
  const isAdmin = isPermissiveAsAccountManager(permissions);
  const isSwitchDisabled = !config.isAuthenticated || !isAdmin || isUnsupportedService || loading;

  const handleSwitchClick = event => {
    setPendingChecked(event.target.checked);
    setShowDialog(true);
  };

  const handleConfirm = async () => {
    setShowDialog(false);
    const previousChecked = checked;
    setChecked(pendingChecked);
    setLoading(true);

    try {
      await dataProvider(CREATE, `tenants/me/switch-calendar-service`, {
        data: { calendarService: pendingChecked ? 2 : 0 },
      });
      const successMessage = pendingChecked
        ? 'calendarServiceConfig.switchedToGraph'
        : 'calendarServiceConfig.switchedToEws';
      showNotification(successMessage, 'info');
      await getConfig();
    } catch (error) {
      setChecked(previousChecked);
      showNotification('ra.message.error', 'warning');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setShowDialog(false);
    setPendingChecked(null);
  };

  const onFocus = async () => {
    if (!isFocus) {
      await getConfig();
    }
    setIsFocus(true);
  };

  const onBlur = () => {
    setIsFocus(false);
  };

  return (
    <MuiThemeProvider theme={lightTheme}>
      <Card onFocus={onFocus} onBlur={onBlur}>
        <List>
          <ListItem>
            <ListItemText primary={translate('calendarServiceConfig.title')} />
          </ListItem>
          {isAdmin && (
            <ListItem style={contentStyle}>
              <div>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={consentAppPageOpen}
                  style={{ textTransform: 'none', minWidth: 250 }}
                >
                  {translate('calendarServiceConfig.approveGraphPermission')}
                </Button>
                <div style={{ padding: '8px 0 0 0', color: 'gray', fontSize: '0.85em' }}>
                  {translate('calendarServiceConfig.approveGraphPermissionNote')}
                </div>
              </div>
            </ListItem>
          )}
          <ListItem style={{ ...contentStyle, flexDirection: 'column', alignItems: 'flex-start' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              <SwitchInput
                label="calendarServiceConfig.useGraphApi"
                toggle={checked}
                onChange={handleSwitchClick}
                disabled={isSwitchDisabled}
              />
              {config.isGraphApiWarningVisible && (
                <Typography
                  variant="body2"
                  color="error"
                  component="div"
                  style={{
                    marginTop: 6,
                    padding: '4px 0 4px 12px',
                    borderLeft: '3px solid currentColor',
                    maxWidth: 600,
                    whiteSpace: 'pre-line',
                    fontWeight: 500,
                  }}
                >
                  {translate('calendarServiceConfig.plannedCustomerNotice')}
                </Typography>
              )}
            </div>
            {isUnsupportedService && (
              <div style={{ padding: '4px 0 0 16px', color: 'gray' }}>
                {translate('calendarServiceConfig.serviceUnavailable')}
              </div>
            )}
            {!config.isAuthenticated && !isUnsupportedService && (
              <div style={{ padding: '4px 0 0 16px', color: 'gray' }}>
                {translate('calendarServiceConfig.authRequired')}
              </div>
            )}
          </ListItem>
        </List>
      </Card>
      <Dialog fullWidth open={showDialog} onClose={handleCancel}>
        <DialogContent>
          <DialogContentText component="div">
            <p>{translate('calendarServiceConfig.confirmDialogTitle')}</p>
            <ul>
              <li>{translate('calendarServiceConfig.confirmDialogNote1')}</li>
              <li>{translate('calendarServiceConfig.confirmDialogNote2')}</li>
            </ul>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={handleConfirm} color="primary">
            {translate('calendarServiceConfig.confirmSwitch')}
          </Button>
          <Button onClick={handleCancel}>{translate('ra.action.cancel')}</Button>
        </DialogActions>
      </Dialog>
    </MuiThemeProvider>
  );
};

const enhance = compose(translate, connect(undefined, { showNotification }));
export default enhance(CalendarServiceConfig);
